import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Rocket, Repeat, Search, KeyRound, BellRing, Activity, CandlestickChart,
  Sun, Moon, Menu, UserCircle, LogOut, Gamepad2, Wallet, Coins,
  X as CloseIcon, MessageSquare, HelpCircle, Sprout, ArrowLeftRight, Trophy, CalendarDays, Layers, ShieldCheck,
  Sigma,
} from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useEscape } from '../utils/useEscape';

import { useThemeStore } from '../store/useThemeStore';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../i18n/LanguageSwitcher';
import i18n from '../i18n';
import { useAppStore } from '../store/useStore';
import { canSeeXStocks } from '../utils/xstocksAccess';
import { canSeeOptions } from '../utils/optionsAccess';
import { isAdmin } from '../utils/adminAccess';
import { apiClient } from '../api/client';
import { Avatar } from '../components/ProfileCard';
import { cn } from '../ui/cn';
import { NotificationsBell } from '../components/NotificationsBell';
import { useMediaQuery } from '../utils/useMediaQuery';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Короткая подпись для нижней навигации на телефоне (см. PRIMARY_NAV). */
  short?: string;
  /** В нижней панели телефона места нет — пункт уходит в «Ещё». */
  desktopOnly?: boolean;
}

const PRIMARY_NAV: NavItem[] = [
  { to: '/',         label: i18n.t('appHeader:nav.terminal'),   icon: Rocket },
  { to: '/auto',     label: i18n.t('appHeader:nav.auto'), short: i18n.t('appHeader:nav.autoShort'),   icon: Repeat },
  { to: '/scanner',  label: i18n.t('appHeader:nav.scanner'),     icon: Search },
  { to: '/funding',  label: i18n.t('appHeader:nav.funding'),    icon: Activity },
  // Токенизированные акции xStocks: все сети и биржи, связки через мост.
  { to: '/xstocks',  label: i18n.t('appHeader:nav.xstocks'),    icon: Layers, desktopOnly: true },
  // Вилки «ставка НЕТ на предикт-рынке + опцион» (реверсивные хеджи).
  { to: '/options',  label: i18n.t('appHeader:nav.options'),    icon: Sigma, desktopOnly: true },
  { to: '/farms',    label: i18n.t('appHeader:nav.farms'),   icon: Sprout },
  { to: '/charts',   label: i18n.t('appHeader:nav.charts'),    icon: CandlestickChart },
];

const SECONDARY_NAV: NavItem[] = [
  { to: '/portfolio',  label: i18n.t('appHeader:nav.portfolio'), icon: Wallet },
  // Балансы — отдельным пунктом: в портфеле они лежат в самом низу под
  // сделками, а «сколько денег на биржах прямо сейчас» спрашивают чаще, чем
  // листают историю. Страница умеет опросить биржи по кнопке, не ждя минуту.
  { to: '/balances',   label: i18n.t('appHeader:nav.balances'),  icon: Coins },
  // Мост LayerZero OFT: перекинуть свои токены между сетями через кошелёк.
  { to: '/bridge',     label: i18n.t('appHeader:nav.bridge'),    icon: ArrowLeftRight },
  // Лидерборд: кто сколько закрыл за период. По умолчанию все анонимны.
  { to: '/leaderboard', label: i18n.t('appHeader:nav.leaderboard'), icon: Trophy },
  { to: '/alerts',     label: i18n.t('appHeader:nav.alerts'),   icon: BellRing },
  // Листинги, запуск торгов, делистинги, разлоки — см. pages/Calendar.
  { to: '/calendar',   label: i18n.t('appHeader:nav.calendar'), icon: CalendarDays },
  { to: '/api-keys',   label: i18n.t('appHeader:nav.apiKeys'), icon: KeyRound },
  // Чат и игры — здесь, а не внутри профиля: профиль это «кто я», а чат и
  // партии — общение, ради них открывать свою карточку незачем.
  { to: '/chat',       label: i18n.t('appHeader:nav.chat'),      icon: MessageSquare },
  { to: '/games',      label: i18n.t('appHeader:nav.games'),     icon: Gamepad2 },
  { to: '/help',       label: i18n.t('appHeader:nav.help'),   icon: HelpCircle },
  // Админка — только админам (navOk прячет остальным).
  { to: '/admin',      label: i18n.t('appHeader:nav.admin'),  icon: ShieldCheck },
];

/** «Ещё» на телефоне: пункты основного меню, которым нет места внизу, и второстепенные. */
const MOBILE_MORE_NAV: NavItem[] = [...PRIMARY_NAV.filter(n => n.desktopOnly), ...SECONDARY_NAV];

export const AppHeader: React.FC = () => {
  const nav = useNavigate();
  // Колокольчик стоит и в шапке компьютера, и в шапке телефона; скрытая CSS-ом
  // половина всё равно монтировалась и опрашивала сервер — каждые 15 с три
  // лишних запроса на любой странице. Рисуем только тот, что виден.
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const { pathname } = useLocation();
  const { theme, toggle: toggleTheme } = useThemeStore();
  const { t: tc } = useTranslation('common');
  const { t } = useTranslation('appHeader');
  const user = useAppStore((s) => s.user);
  const xsOk = canSeeXStocks(user);
  const adminOk = isAdmin(user);
  const optOk = canSeeOptions(user);
  const navOk = (n: NavItem) => (n.to !== '/xstocks' || xsOk) && (n.to !== '/options' || optOk) && (n.to !== '/admin' || adminOk);
  const setAuth = useAppStore((s) => s.setAuthenticated);

  const isActive = (to: string) =>
    to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(to + '/');


  const logout = () => {
    apiClient.clearAuth();
    setAuth(false);
    nav('/login');
  };

  // «Меню» dropdown — собираем SECONDARY_NAV в один компактный popover чтобы
  // не висели 6 кнопок в шапке (Routes/Alerts/Funding/Heatmap/PnL/API Keys).
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Импорт чужих ссылок живёт ТОЛЬКО в конвертере на графике
  // (components/LinkConverter.tsx): там же и обратное направление — открыть
  // текущую пару в fundoor/uainvest. Раньше здесь дублировался диалог
  // «Импорт ссылки» со своим парсером — убран, всё уникальное перенесено туда.
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  // Профиль dropdown: «Открыть профиль» (→ /profile) и «Выйти». /settings
  // вычистили — теперь это страница профиля юзера, не «настройки приложения».
  const [profOpen, setProfOpen] = useState(false);
  const profRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!profOpen) return;
    const onDown = (e: MouseEvent) => {
      if (profRef.current && !profRef.current.contains(e.target as Node)) setProfOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [profOpen]);

  // Мобильное drawer-меню: на телефоне в bottom-nav помещаются только 4
  // PRIMARY_NAV + кнопка «Ещё». До этого drawer'а Routes/Портфель/Alerts/
  // PnL/API Keys/Профиль/Logout вообще не были доступны с
  // мобильного устройства — единственный пункт меню вёл на /snake.
  // Закрываем drawer по нав-ссылке, кнопке X, клику по backdrop'у.
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Body scroll lock пока drawer открыт — иначе под backdrop'ом страница
  // прокручивается пальцем, выглядит как глюк.
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [mobileMenuOpen]);

  // 29.09: Esc закрывает «Меню», профиль и мобильное «Ещё» — раньше только
  // клик мимо, а с клавиатуры из меню было не выйти.
  useEscape(menuOpen, () => setMenuOpen(false));
  useEscape(profOpen, () => setProfOpen(false));
  useEscape(mobileMenuOpen, () => setMobileMenuOpen(false));

  // Ползущий индикатор под активной вкладкой: меряем ссылку по DOM (подписи
  // разной ширины и меняются с языком). Вне главных вкладок — прячем.
  const navRef = useRef<HTMLElement | null>(null);
  const linkRefs = useRef<Record<string, HTMLAnchorElement | null>>({});
  const [thumb, setThumb] = useState<{ left: number; width: number } | null>(null);
  const activeTo = PRIMARY_NAV.find(n => isActive(n.to))?.to ?? null;
  useLayoutEffect(() => {
    const box = navRef.current;
    if (!box) return;
    const measure = () => {
      const a = activeTo ? linkRefs.current[activeTo] : null;
      setThumb(a ? { left: a.offsetLeft, width: a.offsetWidth } : null);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(box);
    return () => ro.disconnect();
  }, [activeTo]);


  return (
    <>
      <header className="sb-header sticky top-0 z-50 w-full border-b border-border/60 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-[1400px] items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* ---------------- DESKTOP ---------------- */}
          <div className="hidden w-full items-center justify-between lg:flex">
            {/* Brand: glyph + monospace wordmark */}
            <Link to="/" className="sb-logo flex flex-shrink-0 items-center gap-2.5 text-foreground">
              {/* Аватар бота вместо значка ⇄ — тот же, что во вкладке браузера. */}
              <img src="/logo.png" alt="" className="h-7 w-7 rounded-lg object-cover ring-1 ring-border" />
              <span className="font-mono text-[15px] font-semibold tracking-tight leading-none">
                spread<span className="text-primary">.</span><span className="text-muted-foreground">bot</span>
              </span>
            </Link>

            {/* Primary nav: icon + label, active = subtle bg + bottom-underline */}
            <nav ref={navRef} aria-label={t('mainMenu')} className="sb-nav">
              {thumb && <span className="sb-nav-thumb" style={{ left: thumb.left, width: thumb.width }} />}
              {PRIMARY_NAV.filter(navOk).map(({ to, label, icon: Icon }) => (
                <Link
                  key={to}
                  to={to}
                  ref={el => { linkRefs.current[to] = el; }}
                  data-on={isActive(to)}
                  className="sb-nav-link"
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span>{label}</span>
                </Link>
              ))}
            </nav>

            {/* Right cluster: «Меню» dropdown (заменяет 6 secondary-кнопок),
                bell, утилиты-dropdown (theme/settings/logout под шестерёнкой). */}
            {/* Правый блок — одна капсула, как вкладки: меню | колокольчик,
                язык, тема | аватар. */}
            <div className="sb-nav !gap-1 !py-1 !pl-1 !pr-1.5">
              {/* «Меню» — все Secondary nav пункты в одном popover'е */}
              <div ref={menuRef} className="relative">
                <button type="button" onClick={() => setMenuOpen(o => !o)}
                  className={cn(
                    'sb-iconbtn gap-1.5 px-3 text-[12px] font-medium',
                    (SECONDARY_NAV.some(n => isActive(n.to)) || menuOpen) && '!bg-primary/10 !text-foreground ring-1 ring-primary/40',
                  )}>
                  <Menu className="h-3.5 w-3.5" />
                  <span>{t('menu')}</span>
                </button>
                {menuOpen && (
                  <div className="sb-pop absolute right-0 top-full z-50 mt-2 grid w-[360px] grid-cols-3 gap-1 rounded-2xl border border-border bg-popover/95 p-2 shadow-2xl backdrop-blur-xl"
                       style={{ transformOrigin: 'top right' }}>
                    {SECONDARY_NAV.filter(navOk).map(({ to, label, icon: Icon }) => (
                      <Link key={to} to={to} onClick={() => setMenuOpen(false)}
                        data-on={isActive(to)} className="sb-tile">
                        <span><Icon className="h-4 w-4"/></span>
                        <span className="leading-tight">{label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <span className="mx-0.5 h-5 w-px bg-border" />
              {isDesktop && <NotificationsBell />}

              <LanguageSwitcher compact className="[&_.sb-seg]:!border-transparent [&_.sb-seg]:!bg-transparent"/>

              {/* Theme toggle — прямая кнопка, без dropdown'a */}
              <button type="button" onClick={toggleTheme}
                aria-label={tc('theme.toggle')}
                className="sb-iconbtn">
                <span key={theme} className="sb-pop inline-flex">
                  {theme === 'dark' ? <Sun className="h-[18px] w-[18px]"/> : <Moon className="h-[18px] w-[18px]"/>}
                </span>
              </button>

              {user && <span className="mx-0.5 h-5 w-px bg-border" />}
              {user && (
                <div ref={profRef} className="relative">
                  <button type="button" onClick={() => setProfOpen(o => !o)}
                    title={user.username}
                    aria-label={t('profile')}
                    className={cn(
                      'inline-flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-card text-[11px] font-semibold text-foreground ring-2 transition-all',
                      profOpen ? 'ring-primary' : 'ring-border hover:ring-primary/60',
                    )}>
                    {/* Аватар вместо буквы: он уже загружен в профиле, а в
                        шапке всё это время висела заглушка. Avatar сам
                        откатится на букву, если картинки нет. */}
                    <Avatar username={user.username} has size={32} />
                  </button>
                  {profOpen && (
                    <div className="sb-pop absolute right-0 top-full z-50 mt-2 w-48 rounded-xl border border-border bg-popover/95 p-1 shadow-2xl backdrop-blur-xl"
                         style={{ transformOrigin: 'top right' }}>
                      <Link to={`/profile/${user.username}`} onClick={() => setProfOpen(false)}
                        className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground/90 hover:bg-accent/60 transition-colors">
                        <UserCircle className="h-3.5 w-3.5 text-muted-foreground"/>
                        <span>{t('openProfile')}</span>
                      </Link>
                      {/* Игры переехали в профиль: там же чат и приглашения,
                          и меню не растёт от каждой новой забавы. */}
                      <Link to="/games" onClick={() => setProfOpen(false)}
                        className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground/90 hover:bg-accent/60 transition-colors">
                        <Gamepad2 className="h-3.5 w-3.5 text-muted-foreground"/>
                        <span>{t('gamesAndChat')}</span>
                      </Link>
                      <div className="my-1 border-t border-border"/>
                      <button type="button"
                        onClick={() => { setProfOpen(false); logout(); }}
                        className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-trade-red hover:bg-trade-red/10 transition-colors">
                        <LogOut className="h-3.5 w-3.5"/>
                        <span>{t('logout')}</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ---------------- MOBILE top row ---------------- */}
          <div className="relative flex w-full items-center justify-between lg:hidden">
            <Link to="/" className="flex items-center gap-2 text-foreground">
              {/* Аватар бота вместо значка ⇄ — тот же, что во вкладке браузера. */}
              <img src="/logo.png" alt="" className="h-6 w-6 rounded-md object-cover" />
              <span className="font-mono text-[13px] font-semibold tracking-tight leading-none">
                spread<span className="text-muted-foreground">.bot</span>
              </span>
            </Link>
            <div className="flex items-center">
              {/* Bell в мобильной шапке — тот же компонент что на desktop'е,
                  чтобы счётчик корректно сбрасывался по клику. Раньше тут была
                  пустая кнопка с фейковой красной точкой, не сбрасывалась. */}
              {!isDesktop && <NotificationsBell />}
              <LanguageSwitcher compact/>
              <button
                type="button"
                onClick={toggleTheme}
                // 40px — мобильная шапка, под палец (было 32px).
                className="inline-flex h-10 w-10 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                aria-label={tc('theme.toggle')}
              >
                {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ---------------- MOBILE bottom nav ---------------- */}
      <nav className="fixed bottom-0 left-0 right-0 z-[60] border-t border-border bg-background/95 backdrop-blur-xl lg:hidden">
        <div className="mx-auto flex h-14 max-w-[1400px] items-center justify-around px-2">
          {PRIMARY_NAV.filter(n => !n.desktopOnly).map(({ to, label, short, icon: Icon }) => {
            const active = isActive(to);
            return (
              <Link
                key={to}
                to={to}
                className={cn(
                  'flex h-full min-w-0 flex-1 flex-col items-center justify-center gap-0.5 transition-colors',
                  active ? 'sb-mnav-on text-foreground' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className={cn('h-4 w-4 transition-colors', active && 'text-primary')} />
                <span className="w-full truncate px-0.5 text-center text-[10px] font-medium leading-none">
                  {short || label}
                </span>
              </Link>
            );
          })}
          {/* «Ещё» — открывает drawer'ом доступ к Routes/Портфелю/Alerts/
              PnL/API Keys/Профилю/Logout (всё что не помещается в
              bottom-nav). Активна когда юзер на любой из этих страниц. */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className={cn(
              'flex h-full w-14 flex-col items-center justify-center gap-0.5 transition-colors',
              (MOBILE_MORE_NAV.some(n => isActive(n.to))
                || ['/profile', '/security', '/achievements', '/snake', '/chess', '/checkers', '/minesweeper'].some(isActive)
                || mobileMenuOpen)
                ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
            aria-label={t('openMenu')}
          >
            <Menu className="h-4 w-4" />
            <span className="text-[10px] font-medium leading-none">{t('more')}</span>
          </button>
        </div>
      </nav>

      {/* ---------------- MOBILE drawer ---------------- */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[80] lg:hidden">
          {/* backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
          {/* sheet — slide-up from bottom. Высота capped 80svh чтобы пользователь
              видел backdrop и понимал что это overlay (не полноэкранная страница). */}
          <div className="absolute bottom-0 left-0 right-0 max-h-[80svh] overflow-y-auto rounded-t-2xl border-t border-border bg-background pb-[max(env(safe-area-inset-bottom),0.75rem)] shadow-2xl">
            {/* drag-handle + header */}
            <div className="sticky top-0 bg-background/95 backdrop-blur-xl">
              <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-border" />
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-2 min-w-0">
                  {/* Аватар, как на компьютере (была буква). */}
                  {user && <Avatar username={user.username} has size={32} />}
                  <div className="min-w-0">
                    <div className="text-sm font-semibold leading-tight truncate">{user?.username || t('guest')}</div>
                    <Link to={user ? `/profile/${user.username}` : '/profile'} onClick={() => setMobileMenuOpen(false)}
                          className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                      <UserCircle className="h-3 w-3" /> {t('openProfile')}
                    </Link>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(false)}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                  aria-label={t('closeMenu')}
                >
                  <CloseIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* nav grid — 2 column tiles, tap-target ~64px высотой */}
            <div className="grid grid-cols-2 gap-2 px-3 pb-3">
              {MOBILE_MORE_NAV.filter(navOk).map(({ to, label, icon: Icon }) => {
                const active = isActive(to);
                return (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      'flex items-center gap-2 rounded-lg border px-3 py-3 text-sm transition-colors',
                      active
                        ? 'border-primary/50 bg-primary/10 text-foreground'
                        : 'border-border bg-card/40 text-foreground hover:bg-accent',
                    )}
                  >
                    <Icon className={cn('h-4 w-4 shrink-0', active ? 'text-primary' : 'text-muted-foreground')} />
                    <span className="truncate">{label}</span>
                  </Link>
                );
              })}
              <Link
                to="/snake"
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  'flex items-center gap-2 rounded-lg border px-3 py-3 text-sm transition-colors',
                  isActive('/snake')
                    ? 'border-primary/50 bg-primary/10 text-foreground'
                    : 'border-border bg-card/40 text-foreground hover:bg-accent',
                )}
              >
                <Gamepad2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span>{t('snake')}</span>
              </Link>
              <Link
                to="/chess"
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  'flex items-center gap-2 rounded-lg border px-3 py-3 text-sm transition-colors',
                  isActive('/chess')
                    ? 'border-primary/50 bg-primary/10 text-foreground'
                    : 'border-border bg-card/40 text-foreground hover:bg-accent',
                )}
              >
                <span className="h-4 w-4 shrink-0 inline-flex items-center justify-center text-muted-foreground">♟</span>
                <span>{t('chess')}</span>
              </Link>
              <Link
                to="/checkers"
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  'flex items-center gap-2 rounded-lg border px-3 py-3 text-sm transition-colors',
                  isActive('/checkers')
                    ? 'border-primary/50 bg-primary/10 text-foreground'
                    : 'border-border bg-card/40 text-foreground hover:bg-accent',
                )}
              >
                <span className="h-4 w-4 shrink-0 inline-flex items-center justify-center text-muted-foreground">⛀</span>
                <span>{t('checkers')}</span>
              </Link>
            </div>

            {/* Footer actions: theme toggle + logout */}
            <div className="border-t border-border px-3 py-2 flex items-center gap-2">
              <button
                type="button"
                onClick={toggleTheme}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-card/40 px-3 py-2.5 text-sm text-foreground hover:bg-accent"
              >
                {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                <span>{theme === 'dark' ? tc('theme.light') : tc('theme.dark')}</span>
              </button>
              {user && (
                <button
                  type="button"
                  onClick={() => { setMobileMenuOpen(false); logout(); }}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-trade-red/40 bg-trade-red/10 px-3 py-2.5 text-sm text-trade-red hover:bg-trade-red/20"
                >
                  <LogOut className="h-4 w-4" />
                  <span>{t('logout')}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

    </>
  );
};
